/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { CSG } from 'three-csg-ts';
import 'javascript-detect-element-resize/detect-element-resize';
import tinycolor from 'tinycolor2';
/* ==== RBF ==== */
export class RBFInterpolator {
    /**
     * @param points Array of 3D points [x,y,z]. Only x and y are used.
     * @param values Array of corresponding z values.
     * @param kernel One of 'cubic', 'gaussian', 'thin-plate', or 'linear'.
     * @param epsilon Shape parameter (for gaussian, used as exp(–(ε·r)²)).
     * @param degree (Optional) Degree of the polynomial augmentation. Defaults to:
     *        1 for 'cubic' and 'thin-plate', 0 for 'linear' and 'gaussian'.
     *        Use -1 to omit polynomial augmentation.
     * @param smoothing (Optional) Smoothing parameter (default 0).
     */
    constructor(points, values, kernel, epsilon, degree, smoothing) {
        if (points.length !== values.length) {
            throw new Error("The number of points must equal the number of values.");
        }
        if (points.length === 0) {
            throw new Error("No points provided.");
        }
        // Use only the first two coordinates (x,y)
        this.data = points.map(p => [p[0], p[1]]);
        this.values = values;
        this.kernel = kernel;
        this.epsilon = epsilon;
        this.smoothing = smoothing !== undefined ? smoothing : 0;
        // Determine a default polynomial degree.
        // SciPy uses a minimum degree of 1 for 'cubic' and 'thin-plate',
        // and 0 for 'linear'. For 'gaussian' (a positive‐definite kernel)
        // a constant term is usually sufficient.
        let minDegree = 0;
        if (kernel === 'cubic' || kernel === 'thin-plate') {
            minDegree = 1;
        }
        this.degree = degree !== undefined ? degree : minDegree;
        // If degree is -1, omit polynomial terms.
        if (this.degree === -1) {
            this.powers = [];
        }
        else {
            this.powers = getMonomialPowers(2, this.degree);
        }
        // Compute shift and scale (for numerical conditioning)
        const cs = computeShiftAndScale(this.data);
        this.shift = cs.shift;
        this.scale = cs.scale;
        // Build the scaled coordinates: (point - shift) / scale.
        this.dataScaled = this.data.map(p => [
            (p[0] - this.shift[0]) / this.scale[0],
            (p[1] - this.shift[1]) / this.scale[1]
        ]);
        // Build the augmented linear system.
        const n = this.dataScaled.length;
        const m = this.powers.length;
        const size = n + m;
        const M = [];
        for (let i = 0; i < size; i++) {
            M.push(new Array(size).fill(0));
        }
        // Fill the RBF kernel matrix (upper-left n×n block).
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                const dx = this.dataScaled[i][0] - this.dataScaled[j][0];
                const dy = this.dataScaled[i][1] - this.dataScaled[j][1];
                const r = Math.hypot(dx, dy);
                let val = rbfKernel(r, this.kernel, this.epsilon);
                // Add smoothing (only on the diagonal).
                if (i === j && this.smoothing !== 0) {
                    val += this.smoothing;
                }
                M[i][j] = val;
            }
        }
        // Fill in the polynomial matrix (P) and its transpose.
        // Upper-right block: for each data point, evaluate all monomials.
        for (let i = 0; i < n; i++) {
            const polyVals = evaluateMonomials(this.dataScaled[i], this.powers);
            for (let j = 0; j < m; j++) {
                M[i][n + j] = polyVals[j];
            }
        }
        // Lower-left block: transpose of the above.
        for (let i = 0; i < m; i++) {
            for (let j = 0; j < n; j++) {
                const polyVals = evaluateMonomials(this.dataScaled[j], this.powers);
                M[n + i][j] = polyVals[i];
            }
        }
        // Lower-right m×m block remains zero.
        // Build right-hand side vector: first n entries are the values, then m zeros.
        const rhs = [];
        for (let i = 0; i < n; i++) {
            rhs.push(this.values[i]);
        }
        for (let i = 0; i < m; i++) {
            rhs.push(0);
        }
        // Solve the system M * coeffs = rhs.
        this.coeffs = solveSystem(M, rhs);
    }
    /**
     * Evaluate the interpolator at (x,y).
     * @param x x-coordinate.
     * @param y y-coordinate.
     * @returns The interpolated value.
     */
    evaluate(x, y) {
        // Scale the input point.
        const xScaled = (x - this.shift[0]) / this.scale[0];
        const yScaled = (y - this.shift[1]) / this.scale[1];
        const p = [xScaled, yScaled];
        const n = this.dataScaled.length;
        const m = this.powers.length;
        let result = 0;
        // Sum the contributions from the RBF terms.
        for (let i = 0; i < n; i++) {
            const dx = p[0] - this.dataScaled[i][0];
            const dy = p[1] - this.dataScaled[i][1];
            const r = Math.hypot(dx, dy);
            result += this.coeffs[i] * rbfKernel(r, this.kernel, this.epsilon);
        }
        // Sum the contributions from the polynomial terms.
        if (m > 0) {
            const polyVals = evaluateMonomials(p, this.powers);
            for (let j = 0; j < m; j++) {
                result += this.coeffs[n + j] * polyVals[j];
            }
        }
        return result;
    }
}
/**
 * Compute a shift (mean) and scale (range) for a set of 2D points.
 */
function computeShiftAndScale(points) {
    const n = points.length;
    const ndim = points[0].length;
    const shift = new Array(ndim).fill(0);
    const minVal = new Array(ndim).fill(Infinity);
    const maxVal = new Array(ndim).fill(-Infinity);
    for (const p of points) {
        for (let d = 0; d < ndim; d++) {
            shift[d] += p[d];
            if (p[d] < minVal[d])
                minVal[d] = p[d];
            if (p[d] > maxVal[d])
                maxVal[d] = p[d];
        }
    }
    for (let d = 0; d < ndim; d++) {
        shift[d] /= n;
    }
    const scale = new Array(ndim).fill(0);
    for (let d = 0; d < ndim; d++) {
        scale[d] = maxVal[d] - minVal[d];
        if (scale[d] === 0) {
            scale[d] = 1;
        }
    }
    return { shift, scale };
}
/**
 * Generate all monomial exponent arrays in "ndim" dimensions whose total degree is ≤ "degree".
 * For example, for ndim=2 and degree=1, this returns [[0,0], [0,1], [1,0]].
 */
function getMonomialPowers(ndim, degree) {
    const powers = [];
    if (degree < 0)
        return powers;
    // Recursive helper to partition "total" into "ndim" parts.
    function generate(current, pos, remaining) {
        if (pos === ndim - 1) {
            current.push(remaining);
            powers.push([...current]);
            current.pop();
            return;
        }
        for (let i = 0; i <= remaining; i++) {
            current.push(i);
            generate(current, pos + 1, remaining - i);
            current.pop();
        }
    }
    // For each total degree from 0 up to "degree".
    for (let total = 0; total <= degree; total++) {
        generate([], 0, total);
    }
    return powers;
}
/**
 * Evaluate the polynomial basis functions at point p.
 * @param p Point (array of coordinates).
 * @param powers List of exponent arrays.
 * @returns Array of monomial evaluations.
 */
function evaluateMonomials(p, powers) {
    const vals = [];
    for (const exponents of powers) {
        let prod = 1;
        for (let d = 0; d < p.length; d++) {
            prod *= Math.pow(p[d], exponents[d]);
        }
        vals.push(prod);
    }
    return vals;
}
/**
 * Evaluate the radial basis function given distance r.
 */
function rbfKernel(r, kernel, epsilon) {
    switch (kernel) {
        case "cubic":
            return Math.pow(r, 3);
        case "gaussian":
            return Math.exp(-Math.pow(epsilon * r, 2));
        case "thin-plate":
            return r === 0 ? 0 : Math.pow(r, 2) * Math.log(r);
        case "linear":
            return r;
        default:
            throw new Error(`Unknown kernel: ${kernel}`);
    }
}
/**
 * Solve the linear system M * x = b using Gaussian elimination with partial pivoting.
 * M is assumed to be square.
 */
function solveSystem(M, b) {
    const n = M.length;
    // Build the augmented matrix.
    const A = M.map((row, i) => row.concat(b[i]));
    // Forward elimination.
    for (let i = 0; i < n; i++) {
        // Find pivot.
        let maxRow = i;
        for (let k = i + 1; k < n; k++) {
            if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) {
                maxRow = k;
            }
        }
        // Swap rows.
        const temp = A[i];
        A[i] = A[maxRow];
        A[maxRow] = temp;
        // Check for singularity.
        if (Math.abs(A[i][i]) < 1e-12) {
            throw new Error("Matrix is singular or nearly singular.");
        }
        // Eliminate below.
        for (let k = i + 1; k < n; k++) {
            const factor = A[k][i] / A[i][i];
            for (let j = i; j <= n; j++) {
                A[k][j] -= factor * A[i][j];
            }
        }
    }
    // Back substitution.
    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
        let sum = A[i][n];
        for (let j = i + 1; j < n; j++) {
            sum -= A[i][j] * x[j];
        }
        x[i] = sum / A[i][i];
    }
    return x;
}
export const DEFAULT_COLORS = {
    minColor: '#3498db',
    avgColor: '#514f9d',
    maxColor: '#ed6d05',
    errorColor: '#d53d0e',
    minErrorColor: '#007AA3',
    normalColor: '#27ae60',
    tempMatrixColors: ['#2980b9', '#3498db', '#2ecc71', '#f1c40f', '#d53d0e'],
    moistureMatrixColors: ['#f1c40f', '#87B93A', '#27ae60', "#28988A", '#2980b9'],
    co2MatrixColors: ['#27ae60', '#87b93a', '#f1c40f', "#28988a", '#bf24b9'],
    errorValueColor: '#c0392b',
    hasAlarmsColor: '#ffb72d',
    mainGrey: '#848A90',
    disableColor: '#777777',
    normalGreyColor: '#384044'
};
export const DEFAULT_MATERIAL = {
    color: '#ffffff',
    emissive: '#dedede',
    sheenColor: '#8f8f8f',
    specularColor: '#ffffff',
    envMapIntensity: 1,
    roughness: 0.85,
    metalness: 1,
    ior: 1,
    reflectivity: 0.5,
    iridescence: 0.15,
    iridescenceIOR: 1,
    sheen: 1,
    sheenRoughness: 1,
    clearcoat: 0,
    clearcoatRoughness: 0,
    specularIntensity: 0.5
};
function BoundingBox(object, box = null) {
    if (box == null) {
        box = new THREE.Box3();
    }
    let _box$3 = new THREE.Box3(new THREE.Vector3(0), new THREE.Vector3(0));
    object.updateWorldMatrix(false, false);
    if (object.userData.ignoreBBox) {
        return box;
    }
    if (object instanceof THREE.Mesh || object instanceof THREE.Sprite) {
        let geometry = object.geometry;
        if (geometry.boundingBox === null) {
            geometry.computeBoundingBox();
        }
        if (geometry.boundingBox && !geometry.boundingBox.isEmpty()) {
            _box$3.copy(geometry.boundingBox);
            _box$3.applyMatrix4(object.matrixWorld);
        }
        box = box.union(_box$3);
    }
    else {
        _box$3.applyMatrix4(object.matrixWorld);
    }
    const children = object.children;
    for (let i = 0, l = children.length; i < l; i++) {
        let child = children[i];
        box = BoundingBox(child, box);
    }
    return box;
}
function isAlarm(opt) {
    return opt.highestAlarmSeverity == 'CRITICAL' && opt.highestAlarmStatus == 'ACTIVE_UNACK';
}
function isStale(opt) {
    return opt.highestAlarmSeverity == 'CRITICAL' && opt.highestAlarmStatus == 'ACTIVE_ACK';
}
function isCableDevice(type) {
    return type == 'moisture_sensor_lines' || type == 'temperature_sensor_lines' || type == 'co2_sensor_lines';
}
function parseJson(value) {
    if (typeof value == 'string') {
        return JSON.parse(value);
    }
    return value;
}
function latestTelemetry(telemetry, _default = null) {
    if (!telemetry)
        return _default;
    if (telemetry.length == 0)
        return _default;
    return telemetry.slice(-1)[0].value;
}
function isCylinder(geo) {
    return geo.type == 'cylinder';
}
function isBuilding(geo) {
    return geo.type == 'building';
}
function flipNormals(geometry) {
    let temp;
    for (let i = 0; i < geometry.index.array.length; i += 3) {
        // swap the first and third values
        temp = geometry.index.array[i];
        geometry.index.array[i] = geometry.index.array[i + 2];
        geometry.index.array[i + 2] = temp;
    }
    geometry.computeVertexNormals();
}
export function toRadians(d) {
    return d * (Math.PI / 180.0);
}
class Compass extends THREE.Group {
    constructor(geometry, options) {
        super();
        this.geometry = geometry;
        this.options = options;
        this.material = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
        this.material.color = new THREE.Color(0x2c3e50);
        let siloType = geometry.type;
        let ring_radius = 10;
        let ring_radius_out = 10;
        let arrow_size = 0;
        let silo_base_length = 10;
        if (isCylinder(geometry)) {
            silo_base_length = geometry.diameter * 0.8;
            ring_radius = silo_base_length * 0.5 * 1.5;
            ring_radius_out = ring_radius + ring_radius * 0.1;
            arrow_size = ring_radius * 0.75;
        }
        else if (isBuilding(geometry)) {
            silo_base_length = Math.sqrt(Math.pow(geometry.width, 2) + Math.pow(geometry.length, 2));
            ring_radius = silo_base_length * 0.5 * 1.2;
            ring_radius_out = ring_radius + ring_radius * 0.05;
            arrow_size = ring_radius * 0.35;
        }
        else {
            this.options.logger.warn('unknown geoemetry: ', geometry);
        }
        let ring_geometry = new THREE.RingGeometry(ring_radius, ring_radius_out, 32, 1, 0, Math.PI * 2.0);
        let ring_mesh = new THREE.Mesh(ring_geometry, this.material);
        ring_mesh.rotation.x = Math.PI / 2;
        this.add(ring_mesh);
        let arrow_geometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
            0, 0, -arrow_size * 0.5,
            0, 0, arrow_size * 0.5,
            arrow_size, 0, 0
        ]);
        const indices = [
            0, 1, 2
        ];
        arrow_geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        arrow_geometry.setIndex(indices);
        arrow_geometry.computeBoundingSphere();
        arrow_geometry.computeVertexNormals();
        let arrow_mesh = new THREE.Mesh(arrow_geometry, this.material);
        arrow_mesh.position.x = ring_radius_out * 1.1;
        this.add(arrow_mesh);
    }
}
function createRenderer(sceneCanvas, bound) {
    // Renderer and canvas
    let renderer = new THREE.WebGLRenderer({ antialias: true, canvas: sceneCanvas });
    renderer.setPixelRatio(window.devicePixelRatio); //eslint-disable-line
    renderer.setSize(bound.width, bound.height);
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    THREE.ColorManagement.enabled = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    return renderer;
}
class RotatingCanvasTexture {
    constructor(src, size = 256, pivotU = 0.5, pivotV = 0.5) {
        this.angle = 0;
        this.pivotU = 0.5; // 0..1 from left
        this.pivotV = 0.5; // 0..1 from top
        this.size = size;
        this.pivotU = pivotU;
        this.pivotV = pivotV;
        this.canvas = document.createElement('canvas');
        this.canvas.width = size;
        this.canvas.height = size;
        const ctx = this.canvas.getContext('2d');
        if (!ctx)
            throw new Error('2D context not available');
        this.ctx = ctx;
        this.ctx.imageSmoothingEnabled = true;
        this.texture = new THREE.CanvasTexture(this.canvas);
        this.texture.colorSpace = THREE.SRGBColorSpace;
        this.texture.needsUpdate = true;
        this.img = new Image();
        this.img.crossOrigin = 'anonymous';
        this.img.onload = () => this.redraw();
        this.img.src = src;
    }
    setAngle(rad) {
        this.angle = rad;
        this.redraw();
    }
    setPivot(u, v) {
        this.pivotU = u;
        this.pivotV = v;
        this.redraw();
    }
    redraw() {
        const s = this.size;
        const c = this.ctx;
        c.clearRect(0, 0, s, s);
        // where the pivot lives in canvas pixels
        const pivotX = this.pivotU * s;
        const pivotY = this.pivotV * s;
        // fit image into canvas (contain) and keep pivot aligned
        const iw = this.img.naturalWidth || this.img.width;
        const ih = this.img.naturalHeight || this.img.height;
        const scale = Math.min(s / iw, s / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        // draw so that the image’s (u,v) lands exactly at (pivotX,pivotY)
        const dx = pivotX - this.pivotU * dw;
        const dy = pivotY - this.pivotV * dh;
        c.save();
        c.translate(pivotX, pivotY);
        c.rotate(this.angle);
        c.translate(-pivotX, -pivotY);
        c.drawImage(this.img, dx, dy, dw, dh);
        c.restore();
        this.texture.needsUpdate = true;
    }
}
class AssetEntityObject extends THREE.Group {
    constructor(ctx, state) {
        super();
        this.ctx = ctx;
        this.state = state;
        this.object = null;
        //options: ISiloRenderOptions
        // TODO: replace these with just telemetry values
        this.sensorsData = {};
        this.disabledSensors = {};
        this.crop = {
            algorithm: 'rbf', angleDirection: 'up',
            cropPoints: [], glidingAngle: 25, margin: 0
        };
        this.selected = false;
        this.headupDisplay = null;
        this.lastUserActivity = performance.now();
        this.userData.isAsset = true;
    }
    get options() {
        return Object.assign(Object.assign({}, this.ctx.options), this.state.options);
    }
    getLabelString() {
        return this.state.name;
    }
    highlight() {
    }
    setState(state) {
        this.state = state;
        this.redraw();
        this.lastUserActivity = performance.now();
    }
    fitInView() {
        if (this.object) {
            this.ctx.fitInView(this.object);
            this.lastUserActivity = performance.now();
        }
    }
    lookAt() {
        if (this.object) {
            this.ctx.lookAt(this.object);
            this.lastUserActivity = performance.now();
        }
    }
    isSelected() {
        return this.selected || false;
    }
    toggleSelect() {
        var _a, _b;
        this.selected = !this.selected;
        if (this.selected) {
            (_a = this.object) === null || _a === void 0 ? void 0 : _a.select();
        }
        else {
            (_b = this.object) === null || _b === void 0 ? void 0 : _b.deselect();
        }
    }
    select() {
        var _a;
        this.selected = true;
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.select();
        this.lastUserActivity = performance.now();
    }
    deselect() {
        var _a;
        this.selected = false;
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.deselect();
        this.lastUserActivity = performance.now();
    }
    updateSensorValues(sensorsData) {
        var _a;
        this.sensorsData = sensorsData;
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.updateSensorValues(sensorsData);
        this.lastUserActivity = performance.now();
    }
    setDisabledSensors(disabledSensors) {
        var _a;
        this.disabledSensors = disabledSensors;
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.setDisabledSensors(disabledSensors);
        this.lastUserActivity = performance.now();
    }
    updateCrop(crop) {
        var _a;
        this.crop = Object.assign(Object.assign({}, this.crop), crop);
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.updateCrop(this.crop);
        this.lastUserActivity = performance.now();
    }
    hightlightSensor(lineNum, sensorIndex) {
        var _a;
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.hightlightSensor(lineNum, sensorIndex);
        this.lastUserActivity = performance.now();
    }
    viewRotate(rotation) {
        var _a;
        (_a = this.object) === null || _a === void 0 ? void 0 : _a.viewRotate(rotation);
        this.lastUserActivity = performance.now();
    }
    redraw() {
        var _a, _b, _c, _d, _e;
        this.clear();
        //console.log('redraw entity')
        const logger = this.ctx.options.logger;
        let opt = this.state;
        const values = opt.attributes;
        const telemetry = opt.telemetry || [];
        //this.options = {...this.ctx.options, ...this.state.options}
        let assetOptions = this.options;
        let sensorLines = [];
        let levelIndicators = [];
        if (values.viewrotate) {
            this.viewRotate(values.viewrotate);
        }
        this.position.set(values.coordinate3dX || 0, values.coordinate3dY || 0, values.coordinate3dZ || 0);
        for (var device of opt.devices) {
            let values = device.attributes;
            if (device.type == 'level_indicator') {
                let cropLevels = {};
                let airLevels = {};
                for (var k in values) {
                    if (k.startsWith('crop-level-')) {
                        let value = Reflect.get(values, k);
                        cropLevels[k] = value;
                    }
                    if (k.startsWith('air-level-')) {
                        let value = Reflect.get(values, k);
                        airLevels[k] = value;
                    }
                }
                let lvl = {
                    cropLevels: cropLevels, airLevels: airLevels, measurePoints: values.measurePoints || []
                };
                levelIndicators.push(lvl);
            }
            else if (isCableDevice(device.type)) {
                let sensorLine = {
                    type: device.type,
                    length: values.length || 0, lineNum: values.lineNum || -1,
                    positionX: values.positionX || 0, positionY: values.positionY || 0,
                    positionZ: values.positionZ || 0, sensorCount: values.sensorCount || 0,
                    sensorDistance: values.sensorDistance || 0,
                    moistureSensors: (_a = parseJson(values.moistureSensors)) === null || _a === void 0 ? void 0 : _a.sensorNumbers,
                    co2Sensors: (_b = parseJson(values.co2Sensors)) === null || _b === void 0 ? void 0 : _b.sensorNumbers,
                    temperatureSensors: (_c = parseJson(values.temperatureSensors)) === null || _c === void 0 ? void 0 : _c.sensorNumbers
                };
                sensorLines.push(sensorLine);
            }
            else {
                logger.warn('unknown device type: ', device.type);
            }
        }
        let obj = null;
        if (opt.type == 'silo') {
            let assetGeo;
            if (!values.eaveheight || !values.totalheight)
                return logger.warn('silo has invalid geometry: ', values);
            if (!values.siloType || values.siloType == 'cylinder') {
                if (!values.diameter)
                    return logger.warn('silo has invalid geometry: ', values);
                let geo = {
                    diameter: values.diameter, eaveheight: values.eaveheight,
                    totalheight: values.totalheight, type: 'cylinder',
                    hopper: values.hopper, hopperangle: values.hopperangle, hopperheight: values.hopperheight
                };
                assetGeo = geo;
                let crop = {
                    margin: 0.25, algorithm: 'rbf',
                    glidingAngle: 0, angleDirection: 'up',
                    cropPoints: latestTelemetry(telemetry['crop-points'], null)
                };
                obj = new SiloObject(geo, sensorLines, crop, assetOptions);
            }
            else if (values.siloType == 'building') {
                if (!values.width || !values.length)
                    return logger.warn('building has invalid geometry: ', values);
                let geo = {
                    eaveheight: values.eaveheight, width: values.width, length: values.length,
                    totalheight: values.totalheight, type: 'building'
                };
                assetGeo = geo;
                let crop = {
                    margin: 0.25, algorithm: 'rbf',
                    glidingAngle: 0, angleDirection: 'up',
                    cropPoints: latestTelemetry(telemetry['crop-points'], null)
                };
                obj = new BuildingObject(geo, sensorLines, crop, assetOptions);
            }
            else {
                return logger.warn('unknown silo type: ', values.siloType);
            }
            if (this.ctx.options.showCompass) {
                this.add(new Compass(assetGeo, this.ctx.options));
            }
        }
        else if (opt.type == 'silo_group') {
            obj = new AssetGroupObject({
                width: (values.width || 10),
                height: (values.height || 5),
                length: (values.length || 15), // + (values.size || 0)
            }, assetOptions);
        }
        else if (opt.type == 'weather_station') {
            obj = new WeatherStationObject({ size: values.size || 5 }, assetOptions);
        }
        else if (opt.type == 'aeration') {
            // NOTE: aeration 3d model is deprecated
            /*
            let wsSize = values.size || 10
            obj = new AerationObject({
              width: wsSize, height: wsSize / 2, length: values.length || 6, scale: values.scale
            }, assetOptions)
            */
            return;
        }
        else {
            return logger.warn('unknown entity type: ', opt.type);
        }
        if (obj != null) {
            obj.levelIndicators = levelIndicators;
            this.object = obj;
            this.add(obj);
            obj.redraw();
            /*
            // TODO: this will stop crop from working, also not sure if it's really needed to do
            try {
              this.updateSensorValues(this.sensorsData)
              this.setDisabledSensors(this.disabledSensors)
              this.updateCrop(this.crop)
            } catch (err) {
              console.warn('failed to redraw telemetry values')
            }
            */
            if (assetOptions.showHeadup) {
                this.add(this.drawHeadupIcon());
            }
        }
        else {
            throw new Error('exepected object');
        }
        if (this.selected) {
            (_d = this.object) === null || _d === void 0 ? void 0 : _d.select();
        }
        else {
            (_e = this.object) === null || _e === void 0 ? void 0 : _e.deselect();
        }
    }
    render() {
        return false; // tell animator that we do not need to re-render
    }
    getIcon(typeIcons) {
        /*let typeIcons = this.options.icons.typeIcons[this.state.type]
        if (this.state.type == 'aeration') {
          typeIcons = this.options.icons.aerationIcons[this.state.attributes.aerationType || 'aeration']
        }*/
        if (isAlarm(this.options)) {
            return typeIcons.alarm;
        }
        else if (isStale(this.options)) {
            return typeIcons.stale;
        }
        else {
            return typeIcons.normal;
        }
    }
    getColor() {
        if (isAlarm(this.options)) {
            return this.options.colors.errorColor;
        }
        else if (isStale(this.options)) {
            return this.options.colors.normalGreyColor;
        }
        else {
            return this.options.colors.normalColor;
        }
    }
    createHUDElement() {
        const icons = this.options.icons;
        let pinImage = this.getIcon(icons.pin);
        let iconImage = this.getIcon(icons.typeIcons[this.state.type]);
        let color = this.getColor();
        // outer element controlled by CSS2DRenderer
        const labelRoot = document.createElement('div');
        labelRoot.style.pointerEvents = 'none';
        let container = document.createElement('div');
        labelRoot.appendChild(container);
        // inner element with your styles
        const labelBox = document.createElement('div');
        labelBox.classList.add('entity-label');
        labelBox.textContent = this.options.label || this.getLabelString();
        let iconBox = document.createElement('div');
        iconBox.style.display = 'flex';
        iconBox.style.alignItems = 'center';
        iconBox.style.justifyContent = 'center';
        let assetIconContainer = document.createElement('div');
        assetIconContainer.style.position = 'relative';
        assetIconContainer.style.display = 'flex';
        let pinIcon = document.createElement('img');
        pinIcon.src = pinImage;
        pinIcon.style.width = "40px";
        assetIconContainer.appendChild(pinIcon);
        let assetIcon = document.createElement('img');
        assetIcon.src = iconImage;
        assetIcon.style.position = 'absolute';
        assetIcon.style.left = '0px';
        assetIcon.style.width = "40px";
        assetIconContainer.appendChild(assetIcon);
        let systemIcons = document.createElement('div');
        systemIcons.style.position = 'absolute';
        systemIcons.style.right = '-14px';
        systemIcons.style.top = "3px";
        systemIcons.style.display = 'flex';
        systemIcons.style.flexDirection = 'column';
        if (this.state.type == 'silo') {
            let sortKeys = {
                'roof': 2, 'aeration': 1, 'alarm': 0
            };
            let systems = (this.state.systems || []).map(systemConfig => {
                return this.ctx.listAssets().find(x => x.state.id == systemConfig.id);
            }).filter(x => x != undefined);
            systems = systems.sort((a, b) => {
                return sortKeys[a.state.attributes.aerationType || 'aeration'] < sortKeys[b.state.attributes.aerationType || 'aeration'] ? 1 : -1;
            });
            systems.map(system => {
                let iconGroup = document.createElement('div');
                iconGroup.style.position = 'relative';
                let attributes = system.state.attributes;
                let aerationType = attributes.aerationType;
                let hasFans = aerationType == 'aeration' || aerationType == 'roof';
                let icons = Reflect.get(this.options.icons.typeIcons, system.state.type);
                if (system.state.type == 'aeration' && aerationType) {
                    icons = this.options.icons.aerationIcons[aerationType];
                }
                let aerationIcon = document.createElement('img');
                aerationIcon.src = system.getIcon(icons); // iconImage
                aerationIcon.style.width = "25px";
                aerationIcon.style.margin = "-2.5px";
                aerationIcon.style.pointerEvents = 'fill';
                aerationIcon.addEventListener('pointerdown', evt => {
                    this.ctx.setHoverAsset(system);
                    evt.preventDefault();
                    return false;
                });
                iconGroup.appendChild(aerationIcon);
                let rotateImage = aerationIcon;
                if (aerationType == 'roof') {
                    let fanImage = system.getIcon(this.options.icons.fanIcons);
                    let fanIcon = document.createElement('img');
                    fanIcon.src = fanImage;
                    fanIcon.style.width = "25px";
                    fanIcon.style.margin = "-2.5px";
                    fanIcon.style.pointerEvents = 'fill';
                    fanIcon.style.position = 'absolute';
                    fanIcon.style.left = "0px";
                    fanIcon.style.top = "0px";
                    fanIcon.addEventListener('pointerdown', evt => {
                        this.ctx.setHoverAsset(system);
                        evt.preventDefault();
                        return false;
                    });
                    iconGroup.appendChild(fanIcon);
                    rotateImage = fanIcon;
                }
                let isAerationOn = this.ctx.aerationStates[system.state.id];
                if (isAerationOn) {
                    if (hasFans) {
                        rotateImage.style.animation = "aerationRotate 2s infinite linear";
                    }
                    else {
                        rotateImage.style.animation = "aerationBlink 2s infinite linear";
                    }
                }
                rotateImage.style.transformOrigin = "50% calc(50% - 1px)";
                systemIcons.appendChild(iconGroup);
            });
        }
        assetIconContainer.appendChild(systemIcons);
        iconBox.appendChild(assetIconContainer);
        const style = {
            border: 'solid ' + color,
            borderRadius: '10px',
            color,
            backgroundColor: '#fff',
            paddingLeft: '5px',
            paddingRight: '5px',
            paddingTop: '3px',
            paddingBottom: '3px',
            fontFamily: "'Roboto', serif",
            fontWeight: '500',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            fontSize: '16px'
        };
        for (const k in style) {
            // @ts-ignore
            labelBox.style[k] = style[k];
        }
        // offset inside the wrapper so bottom sits on anchor
        const pixelGap = 0;
        container.style.transform = `translate(0%, calc(-50% - ${pixelGap}px))`;
        container.style.marginBottom = '10px';
        container.style.pointerEvents = 'fill';
        let clickable = [assetIconContainer, labelBox];
        clickable.map(elm => {
            elm.addEventListener('pointerdown', evt => {
                this.ctx.setHoverAsset(this);
                evt.preventDefault();
                return false;
            });
        });
        container.appendChild(labelBox);
        container.appendChild(iconBox);
        return new CSS2DObject(labelRoot);
    }
    drawHeadupIcon() {
        const result = new THREE.Group();
        if (this.headupDisplay) {
            this.headupDisplay.element.remove();
        }
        this.headupDisplay = this.createHUDElement();
        if (this.headupDisplay != null) {
            result.add(this.headupDisplay);
        }
        const objectTop = this.object ? BoundingBox(this.object).max.y : 0;
        const padWorld = 0; // add padding in meters here if needed
        result.position.set(0, objectTop + padWorld, 0);
        return result;
    }
}
export class Context3D {
    constructor(drawingElement, options) {
        this.options = options;
        this.scene = new THREE.Scene();
        this.animationId = null;
        this.content = new THREE.Group();
        this.environment = new THREE.Group();
        this.assets = {};
        this.activeTool = null;
        this.selectTool = new SelectTool(this);
        this.placementTool = new PlacementTool(this);
        this.id = null;
        this.grid = new THREE.GridHelper(1000, 15);
        this.focusAsset = null;
        this.config = null;
        this.isCameraLoaded = null;
        this.state = 'normal';
        this.history = [];
        this.transaction = {};
        this.adminMode = false;
        this.aerationStates = {};
        this.standby = false;
        this.lastRender = null;
        this.mustAlwaysRender = false;
        this.pauseAnimation = false;
        this.parentElement = drawingElement;
        this.parentElement.style.overflow = 'hidden';
        this.cssRenderer = new CSS2DRenderer();
        this.cssRenderer.domElement.style.position = 'absolute';
        this.cssRenderer.domElement.style.top = '0px';
        this.parentElement.appendChild(this.cssRenderer.domElement);
        this.sceneCanvas = drawingElement.appendChild(document.createElement('canvas'));
        let bound = this.getContainerBound();
        this.aspect = bound.width / bound.height;
        this.renderer = createRenderer(this.sceneCanvas, bound);
        this.camera = new THREE.PerspectiveCamera(55, this.aspect, 0.5, 3000);
        this.scene.add(this.camera);
        this.controls = new OrbitControls(this.camera, this.parentElement);
        this.grid.visible = false;
        this.hdr = new RGBELoader().load(this.options.environment.hdrTexture, () => {
            this.hdr.mapping = THREE.EquirectangularReflectionMapping;
        });
        this.setupCamera();
        this.activateTool(this.selectTool);
        this.resizeHandler = this.resize.bind(this);
        addResizeListener(this.parentElement, this.resizeHandler);
        this.controls.addEventListener('change', (evt) => {
            this.saveCamera();
        });
        this.lastUserActivity = performance.now();
        drawingElement.addEventListener('pointermove', e => {
            this.lastUserActivity = performance.now();
        }, false);
        this.animate();
        // test aeration on / off
        /*setInterval(() => {
          this.listAssets().map(asset => {
            let state = this.aerationStates[asset.state.id]
            if (state) {
              this.updateAerationState(asset.state.id, 'on')
            } else {
              this.updateAerationState(asset.state.id, 'on')
            }
          })
        }, 500)*/
    }
    clearCssElements() {
        // NOTE: css elements are not always removed when the 3d object is removed
        //       need to clear elements manually
        this.cssRenderer.domElement.replaceChildren();
    }
    // NOTE: bit of a hack to make html labels part of tool mechanics
    setHoverAsset(asset) {
        if (!this.selectTool.mouseHoverAsset)
            this.selectTool.mouseHoverAsset = asset;
        if (!this.placementTool.mouseHoverAsset)
            this.placementTool.mouseHoverAsset = asset;
    }
    configure(conf) {
        if (this.adminMode) {
            return console.error('cannot reconfigure 3d scene while in edit-mode');
        }
        this.config = conf;
        this.clear();
        this.options = conf.options;
        this.setId(conf.id);
        conf.assets.map(asset => {
            this.addAsset(asset);
        });
        if (conf.selection) {
            this.selectAsset(conf.selection);
        }
        if (conf.focus) {
            this.lookAtAsset(conf.focus);
        }
        this.redraw();
    }
    lookAtAsset(assetId) {
        this.focusAsset = assetId;
    }
    update(opt) {
        Object.assign(this.options, opt);
    }
    get saveKey() { return "3d-context-" + this.id; }
    setId(id) {
        this.id = id;
    }
    loadCamera() {
        if (this.id == null || this.isCameraLoaded == this.id)
            return;
        let data = localStorage.getItem(this.saveKey);
        try {
            if (data == null)
                throw new Error('');
            let state = JSON.parse(data);
            this.controls.target0.copy(state.target0);
            this.controls.position0.copy(state.position0);
            this.controls.zoom0 = state.zoom0;
            this.controls.reset();
            this.lookAt(this.content);
            this.isCameraLoaded = this.id;
        }
        catch (err) {
            this.fitInView(this.content);
        }
    }
    saveCamera() {
        if (this.id == null || Object.values(this.assets).length == 0)
            return;
        this.controls.saveState();
        const { target0, position0, zoom0 } = this.controls;
        const state = { target0, position0, zoom0 };
        localStorage.setItem(this.saveKey, JSON.stringify(state));
    }
    fitInView(object) {
        const camera = this.camera;
        let center = new THREE.Vector3();
        let size = new THREE.Vector3();
        const boundingBox = BoundingBox(object);
        boundingBox.getCenter(center);
        boundingBox.getSize(size);
        let aim = new THREE.Vector3(center.x, size.y * 0.35, center.z);
        let newPositionCamera = new THREE.Vector3(center.x + size.y, (size.y * 1.2), center.z + size.y);
        camera.position.set(newPositionCamera.x, newPositionCamera.y, newPositionCamera.z);
        camera.lookAt(aim.x, aim.y, aim.z);
        this.controls.object.position.set(newPositionCamera.x, newPositionCamera.y, newPositionCamera.z);
        this.controls.target = aim;
        this.controls.update();
        this.camera.updateProjectionMatrix();
        this.lastUserActivity = performance.now();
    }
    lookAt(object) {
        const boundingBox = BoundingBox(object);
        const camera = this.camera;
        const center = boundingBox.getCenter(new THREE.Vector3());
        let size = boundingBox.getSize(new THREE.Vector3());
        let aim = new THREE.Vector3(center.x, size.y * 0.35, center.z);
        camera.lookAt(aim.x, aim.y, aim.z);
        this.controls.target = aim;
        this.controls.update();
        this.camera.updateProjectionMatrix();
        this.lastUserActivity = performance.now();
    }
    listAssets() {
        return Object.values(this.assets);
    }
    activateTool(tool) {
        if (this.activeTool)
            this.activeTool.dispose();
        this.activeTool = tool;
        this.activeTool.activate();
    }
    addAsset(opt) {
        var _a;
        let key = opt.id;
        let result;
        if (this.assets[key]) {
            this.assets[key].setState(opt);
            result = this.assets[key];
        }
        else {
            let object = new AssetEntityObject(this, opt);
            this.content.add(object);
            this.assets[key] = object;
            result = object;
        }
        if ((_a = opt.options) === null || _a === void 0 ? void 0 : _a.aeration) {
            this.updateAerationState(key, opt.options.aeration);
        }
        this.lastUserActivity = performance.now();
        return result;
    }
    onCameraChange(e) { }
    clear() {
        if (this.adminMode) {
            console.warn('cannot clear while in edit mode');
        }
        this.focusAsset = null;
        this.scene.clear();
        this.clearCssElements();
        this.assets = {};
        this.lastUserActivity = performance.now();
    }
    redrawContent() {
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMapping = THREE.ReinhardToneMapping;
        this.renderer.toneMapping = THREE.CineonToneMapping;
        this.renderer.toneMappingExposure = this.options.environment.exposure;
        this.options.material.envMap = this.hdr;
        this.listAssets().map(asset => asset.clear());
        this.environment.clear();
        this.content.clear();
        this.clearCssElements();
        this.environment.add(setupEnvironment(this.options));
        //this.scene.add(new THREE.AmbientLight(0xfff, 1000))
        for (var k in this.assets) {
            let asset = this.assets[k];
            this.content.add(asset);
            asset.redraw();
        }
        this.grid.visible = this.options.showGrid;
        this.cssRenderer.render(this.scene, this.camera);
    }
    redraw() {
        try {
            // pause animation while building objects
            this.pauseAnimation = true;
            this.scene.clear();
            this.scene.visible = false;
            this.scene.add(this.content, this.environment, this.grid);
            this.redrawContent();
            this.resize();
            this.loadCamera();
            if (this.focusAsset) {
                let asset = this.assets[this.focusAsset];
                if (asset != null) {
                    asset.lookAt();
                }
            }
            this.scene.visible = true;
        }
        catch (err) {
            console.error(err);
        }
        finally {
            this.pauseAnimation = false;
        }
        this.lastUserActivity = performance.now();
    }
    setupCamera() {
        let camera = this.camera;
        let controls = this.controls;
        let camera_position = new THREE.Vector3(0, 10, 0);
        let camera_aim = new THREE.Vector3(0.0, 5, 0.0);
        camera.position.set(camera_position.x, camera_position.y, camera_position.z);
        camera.lookAt(camera_aim);
        controls.minPolarAngle = 0.0;
        controls.maxPolarAngle = Math.PI * 0.5;
        controls.panSpeed = 0.0;
        controls.minDistance = 0;
        controls.maxDistance = 2000.0;
        controls.update();
        controls.object.position.set(camera_position.x, camera_position.y, camera_position.z);
        controls.target = camera_aim;
        this.camera.updateProjectionMatrix();
    }
    dispose() {
        var _a;
        removeResizeListener(this.parentElement, this.resizeHandler);
        (_a = this.activeTool) === null || _a === void 0 ? void 0 : _a.dispose();
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer.forceContextLoss();
        }
        this.sceneCanvas.remove();
        if (this.animationId) {
            try {
                cancelAnimationFrame(this.animationId);
            }
            catch (err) {
                window.clearTimeout(this.animationId);
            }
        }
    }
    getContainerBound() {
        return this.parentElement.getBoundingClientRect();
    }
    resize() {
        let bound = this.getContainerBound();
        if (this.camera) {
            this.camera.aspect = bound.width / bound.height;
            this.camera.updateProjectionMatrix();
        }
        if (this.renderer) {
            this.renderer.setSize(bound.width, bound.height);
        }
        if (this.cssRenderer) {
            this.cssRenderer.setSize(bound.width, bound.height);
        }
        this.lastUserActivity = performance.now();
        //this.fitInView(this.content)
    }
    clearSelection() {
        for (var k in this.assets) {
            this.assets[k].deselect();
        }
        if (!this.adminMode && this.options.onUserSelect) {
            this.options.onUserSelect(null);
        }
        this.lastUserActivity = performance.now();
    }
    save(callback) {
        this.commit();
        let allChanges = {};
        this.history.map(tr => {
            for (let assetId in tr) {
                let assetUpdates = tr[assetId];
                let assetChanges = {};
                for (var key in assetUpdates) {
                    assetChanges[key] = assetUpdates[key].toValue;
                }
                if (Object.keys(assetChanges).length > 0) {
                    if (!allChanges[assetId]) {
                        allChanges[assetId] = {};
                    }
                    Object.assign(allChanges[assetId], assetChanges);
                }
            }
        });
        let assets = this.listAssets().map(x => x.state);
        this.state = 'saving';
        this.emitUpdate();
        callback(allChanges, assets).then(() => {
            this.history = [];
            this.transaction = {};
            this.state = 'normal';
            this.emitUpdate();
        }).catch(err => {
            this.state = 'error';
            this.emitUpdate();
        });
    }
    emitUpdate() {
        if (this.options.onUpdate)
            this.options.onUpdate();
    }
    updateAssets(assets, attributes, commit = false) {
        let tr = this.transaction;
        assets.map(asset => {
            if (!tr[asset.state.id]) {
                tr[asset.state.id] = {};
            }
            let changes = {};
            let change = tr[asset.state.id] || {};
            for (var k in attributes) {
                let currValue = Reflect.get(asset.state.attributes, k);
                let nextValue = Reflect.get(attributes, k);
                if (change[k] != undefined && change[k].fromValue != undefined) {
                    currValue = change[k].fromValue;
                }
                changes[k] = { fromValue: currValue, toValue: nextValue };
            }
            Object.assign(tr[asset.state.id], changes);
            Object.assign(asset.state.attributes, attributes);
            asset.redraw();
        });
        this.lastUserActivity = performance.now();
        if (commit) {
            this.commit();
        }
    }
    canUndo() {
        return this.history.length > 0 || Object.keys(this.transaction).length > 0;
    }
    hasChanges() {
        return this.canUndo();
    }
    commit() {
        // TODO: implement, for doing undo / redo
        if (Object.keys(this.transaction).length > 0) {
            this.history.push(this.transaction);
            this.transaction = {};
            this.emitUpdate();
            this.lastUserActivity = performance.now();
        }
    }
    undo() {
        this.commit();
        let tr = this.history.pop();
        if (!tr)
            return console.warn('nothing to undo');
        this.revert(tr);
        this.emitUpdate();
        this.lastUserActivity = performance.now();
    }
    revert(tr) {
        for (let id in tr) {
            let asset = this.listAssets().find(x => x.state.id == id);
            if (!asset) {
                return console.warn('asset not found', id);
            }
            let updates = {};
            for (let key in tr[id]) {
                updates[key] = tr[id][key].fromValue;
            }
            Object.assign(asset.state.attributes, updates);
            asset.redraw();
        }
    }
    resetHistory() {
        this.commit();
        while (this.history.length > 0) {
            let tr = this.history.pop();
            if (!tr) {
                break;
            }
            this.revert(tr);
        }
        this.redrawContent();
    }
    startAdminMode(onSave, onComplete) {
        if (this.adminMode) {
            this.redrawContent();
            return console.warn('admin mode already started');
        }
        this.options.showGrid = true;
        this.options.showGround = false;
        this.options.showSelection = true;
        this.adminMode = true;
        this.activateTool(this.placementTool);
        let _this = this;
        function adminShortcuts(evt) {
            if (evt.key == 'z' && (evt.ctrlKey || evt.metaKey)) {
                evt.preventDefault();
                _this.undo();
                return false;
            }
            let selection = _this.listAssets().filter(x => x.isSelected());
            if (selection.length == 0) {
                return true;
            }
            let key;
            let direction = 0;
            if (evt.key == 'ArrowUp') {
                key = 'coordinate3dZ';
                direction = -1;
            }
            else if (evt.key == 'ArrowDown') {
                key = 'coordinate3dZ';
                direction = 1;
            }
            else if (evt.key == 'ArrowLeft') {
                key = 'coordinate3dX';
                direction = 1;
            }
            else if (evt.key == 'ArrowRight') {
                key = 'coordinate3dX';
                direction = -1;
            }
            else {
                return true;
            }
            let multiply = 1;
            if (evt.shiftKey || evt.altKey) {
                multiply = 0.1;
            }
            selection.map(asset => {
                let value = Reflect.get(asset.state.attributes, key) || 0;
                _this.updateAssets([asset], { [key]: value + (direction * multiply) });
            });
            evt.preventDefault();
            return false;
        }
        window.addEventListener('keydown', adminShortcuts, { capture: true });
        let gui = new GUI();
        let createUI = () => {
            let selection = this.listAssets().filter(x => x.isSelected());
            let sizeableAssets = selection.filter(x => {
                return ["weather_station"].includes(x.state.type);
            });
            let boxAssets = selection.filter(x => {
                return ["silo_group"].includes(x.state.type);
            });
            gui.destroy();
            gui = new GUI();
            if (this.state == 'saving') {
                gui.title('Saving..');
                return;
            }
            let state = {
                'size': Math.max(...sizeableAssets.map(x => x.state.attributes.size || 0)),
                'length': Math.max(...boxAssets.map(x => x.state.attributes.length || 0)),
                'width': Math.max(...boxAssets.map(x => x.state.attributes.width || 0)),
                'height': Math.max(...boxAssets.map(x => x.state.attributes.height || 0))
            };
            let actions = {
                undo: () => {
                    this.undo();
                },
                exit: () => {
                    dispose(gui);
                },
                alignX: () => {
                    //let geometries = selection.map(obj => obj.object).filter(x => x != null)
                    //let boxes = selection.map(g => BoundingBox(g))
                    // Check if aligning along X would cause overlaps (only check x extent)
                    /*function overlapInX(boxes: THREE.Box3[]) {
                      boxes.find(b1 => {
                        return boxes.find(b2 => {
                          if (b1 == b2) return false
                          return
                        })
                      })
                    }*/
                    let allPoints = selection.map(x => x.state.attributes.coordinate3dX || 0);
                    let origin = Math.round(Math.max(...allPoints));
                    this.updateAssets(selection, { coordinate3dX: origin }, true);
                },
                alignZ: () => {
                    let allPoints = selection.map(x => x.state.attributes.coordinate3dZ || 0);
                    let origin = Math.round(Math.max(...allPoints));
                    this.updateAssets(selection, { coordinate3dZ: origin }, true);
                },
                justify: () => {
                    let boxes = selection.map(g => BoundingBox(g));
                    let minValue = Math.min(...boxes.map(x => x.min.z));
                    let gap = 5;
                    let pos = minValue;
                    let objects = selection.sort((a, b) => {
                        return BoundingBox(a).min.z > BoundingBox(b).min.z ? 1 : 0;
                    });
                    selection.map(asset => {
                        if (!asset.object)
                            return;
                        let bbox = BoundingBox(asset.object);
                        let size = bbox.max.z - bbox.min.z;
                        this.updateAssets([asset], { coordinate3dZ: pos });
                        pos = pos + size + gap;
                    });
                },
                moveToOrigin: () => { },
                save: () => {
                    this.save(onSave);
                }
            };
            let title = selection.map(x => x.state.name).join(' - ');
            gui.title(title.slice(0, 30) || 'no selection');
            gui.add(actions, 'exit');
            gui.add(actions, 'undo').disable(!this.canUndo());
            gui.add(actions, 'save').disable(!this.canUndo());
            let operations = gui.addFolder('Operations');
            operations.add(actions, 'alignX').disable(selection.length < 2);
            operations.add(actions, 'alignZ').disable(selection.length < 2);
            //gui.add(actions, 'justify')
            operations.add(state, 'size', 1, 50, 0.1).onChange(value => {
                this.updateAssets(sizeableAssets, { size: value });
            }).onFinishChange(value => {
                this.commit();
                // TODO: commit here
            }).disable(sizeableAssets.length == 0);
            operations.add(state, 'length', 1, 100, 0.1).onChange(value => {
                this.updateAssets(boxAssets, { length: value });
            }).disable(boxAssets.length == 0);
            operations.add(state, 'width', 1, 100, 0.1).onChange(value => {
                this.updateAssets(boxAssets, { width: value });
            }).disable(boxAssets.length == 0);
            operations.add(state, 'height', 1, 100, 0.1).onChange(value => {
                this.updateAssets(boxAssets, { height: value });
            }).disable(boxAssets.length == 0);
            let aerations = selection.filter(x => x.state.type == 'aeration');
            let testing = gui.addFolder('Testing');
            if (aerations.length > 0) {
                aerations.slice(0, 1).map(aeration => {
                    let id = aeration.state.id;
                    let aerationState = {
                        'aeration on': this.aerationStates[id] || false
                    };
                    testing.add(aerationState, 'aeration on').onChange(value => {
                        this.updateAerationState(id, value);
                    });
                });
            }
            selection.map(asset => {
                let testState = {
                    'alarm': 'normal'
                };
                if (!asset.state.options) {
                    asset.state.options = { label: '' };
                }
                if (isAlarm(asset.state.options)) {
                    testState.alarm = 'alarm';
                }
                else if (isStale(asset.state.options)) {
                    testState.alarm = 'stale';
                }
                testing.add(testState, 'alarm', ['normal', 'alarm', 'stale']).onChange(value => {
                    let opt = asset.state.options;
                    if (value == 'alarm') {
                        opt.highestAlarmSeverity = 'CRITICAL';
                        opt.highestAlarmStatus = 'ACTIVE_UNACK';
                    }
                    else if (value == 'stale') {
                        opt.highestAlarmSeverity = 'CRITICAL';
                        opt.highestAlarmStatus = 'ACTIVE_ACK';
                    }
                    else {
                        opt.highestAlarmSeverity = undefined;
                        opt.highestAlarmStatus = undefined;
                    }
                    this.redrawContent();
                });
            });
        };
        this.options.onUpdate = () => createUI();
        createUI();
        this.redraw();
        let dispose = (gui) => {
            this.resetHistory();
            gui.destroy();
            window.removeEventListener('keydown', adminShortcuts, { capture: true });
            this.options.onUpdate = undefined;
            this.options.showGrid = false;
            this.options.showGround = true;
            this.adminMode = false;
            this.activateTool(this.selectTool);
            this.redrawContent();
            onComplete();
        };
    }
    selectAsset(id, mode = 'replace') {
        let asset = this.assets[id];
        if (!asset)
            return this.options.logger.warn('cannot select, no such asset:', id);
        if (mode == 'add') {
            asset.select();
        }
        else if (mode == 'toggle') {
            asset.toggleSelect();
        }
        else if (mode == 'replace') {
            for (var k in this.assets) {
                this.assets[k].deselect();
            }
            asset.select();
        }
        if (!this.adminMode && this.options.onUserSelect) {
            this.options.onUserSelect(id);
        }
        if (this.options.onUpdate)
            this.options.onUpdate();
        this.lastUserActivity = performance.now();
    }
    updateAerationState(id, isOn) {
        let state = false;
        if (typeof isOn == 'string') {
            state = isOn.toLowerCase() == 'on' ? true : false;
        }
        else {
            state = isOn;
        }
        this.aerationStates[id] = state;
        this.redrawContent();
        this.lastUserActivity = performance.now();
    }
    animate() {
        if (this.pauseAnimation) {
            window.setTimeout(() => this.animate(), 500);
        }
        try {
            this.doAnimate();
        }
        catch (err) {
            console.error(err);
            this.animationId = window.setTimeout(() => {
                this.animate();
            }, 500);
        }
    }
    doAnimate() {
        let time = performance.now();
        let acitivies = [this.lastUserActivity];
        for (var k in this.assets) {
            let assetActivity = this.assets[k].lastUserActivity;
            if (assetActivity && typeof assetActivity == 'number' && !isNaN(assetActivity)) {
                acitivies.push(this.assets[k].lastUserActivity);
            }
        }
        let lastUserActivity = Math.max(...acitivies);
        let shouldRender = !this.lastRender || (time - this.lastRender) > (1000 * 60);
        let shouldStandby = !shouldRender && (time - lastUserActivity) > 1000;
        if (!this.standby && shouldStandby) {
            this.standby = true;
        }
        else if (this.standby && !shouldStandby) {
            this.standby = false;
        }
        // if aerations are running then always render
        let mustAlwaysRender = false;
        for (var k in this.assets) {
            let continueRender = this.assets[k].render();
            mustAlwaysRender = mustAlwaysRender || continueRender;
        }
        this.mustAlwaysRender = mustAlwaysRender;
        if (!this.standby || this.mustAlwaysRender) {
            this.animationId = requestAnimationFrame(() => this.animate());
            if (this.renderer && this.scene && this.camera) {
                this.renderer.render(this.scene, this.camera);
                this.lastRender = time;
            }
            // must always render is a hack to always render rotating aeration icon if running
            this.cssRenderer.render(this.scene, this.camera);
        }
        else {
            this.animationId = window.setTimeout(() => {
                this.animate();
            }, 500);
        }
    }
}
class Tool {
    constructor(ctx) {
        this.ctx = ctx;
        this.cursor = { x: 0, y: 0 };
        this.raycaster = new THREE.Raycaster();
        this.keyEvent = null;
        this.bindings = [];
    }
    clearBindings() {
        for (var bind of this.bindings) {
            bind.element.removeEventListener(bind.type, bind.callback, false);
        }
    }
    dispose() {
        this.clearBindings();
    }
    activate() {
        this.clearBindings();
        let root = this.ctx.parentElement;
        this.bindings = [
            { element: root, type: 'pointerdown', callback: this.onMouseDown.bind(this) },
            { element: window, type: 'pointermove', callback: this.onMouseMove.bind(this) },
            { element: window, type: 'pointerup', callback: this.onMouseUp.bind(this) },
            { element: window, type: 'keydown', callback: (evt) => {
                    this.keyEvent = evt;
                } },
            { element: window, type: 'keyup', callback: (evt) => {
                    this.keyEvent = null;
                } }
        ];
        for (var bind of this.bindings) {
            bind.element.addEventListener(bind.type, bind.callback, false);
        }
        this.onActivate();
    }
    onActivate() { }
    onMouseDown(event) { }
    onMouseMove(event) { }
    onMouseUp(event) { }
    get renderer() { return this.ctx.renderer; }
    get drawingElement() { return this.ctx.parentElement; }
    get camera() {
        return this.ctx.camera;
    }
    get content() { return this.ctx.content; }
    get controls() {
        return this.ctx.controls;
    }
    initRaycaster(event) {
        const camera = this.camera;
        const rect = this.renderer.domElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const containerRect = this.drawingElement.getBoundingClientRect();
        this.cursor.x = (x / containerRect.width) * 2 - 1;
        this.cursor.y = (y / containerRect.height) * -2 + 1;
        this.raycaster.setFromCamera(new THREE.Vector2(this.cursor.x, this.cursor.y), camera);
    }
    getIntersectedObjects(event) {
        this.initRaycaster(event);
        let assetObjects = this.ctx.listAssets().map(x => x.object).filter(x => x != null);
        return this.raycaster.intersectObjects(assetObjects, true);
    }
    getAssetMesh(mesh) {
        let obj = mesh;
        while (obj != null) {
            if (obj.userData.isAsset)
                return obj;
            if (obj.parent == null)
                break;
            obj = obj.parent;
        }
        return null;
    }
    getIntersectedAssets(event) {
        const intersectedObjects = this.getIntersectedObjects(event);
        let assets = intersectedObjects.map(x => this.getAssetMesh(x.object)).filter(x => x != null);
        return assets;
    }
    getAssetIntersect(event) {
        const intersectedObjects = this.getIntersectedObjects(event);
        for (var intersect of intersectedObjects) {
            let asset = this.getAssetMesh(intersect.object);
            if (asset != null) {
                intersect.object = asset;
                return intersect;
            }
        }
        return null;
    }
    getCursorAsset(event) {
        let assets = this.getIntersectedAssets(event);
        if (assets.length > 0)
            return assets[0];
        return null;
    }
}
class SelectTool extends Tool {
    constructor() {
        super(...arguments);
        this.mouseHoverAsset = null;
        this.mouseStartPos = new THREE.Vector2(0);
    }
    onMouseDown(event) {
        this.mouseStartPos.set(event.screenX, event.screenY);
    }
    onMouseUp(event) {
        let vec = new THREE.Vector2(event.screenX, event.screenY);
        let moveVector = this.mouseStartPos.sub(vec);
        let asset = this.mouseHoverAsset;
        if (!asset) {
            asset = this.getCursorAsset(event);
        }
        this.mouseHoverAsset = null;
        //const asset = this.mouseHoverAsset
        if (asset != null && moveVector.length() < 6) {
            this.ctx.selectAsset(asset.state.id, 'replace');
        }
    }
    onMouseMove(event) {
    }
}
class PlacementTool extends Tool {
    constructor() {
        super(...arguments);
        this.mouseHoverAsset = null;
        this.mouseStartPos = new THREE.Vector2(0);
        this.plane = new THREE.Plane();
        this.pNormal = new THREE.Vector3(0, 1, 0);
        this.planeIntersect = new THREE.Vector3();
        this.isDragging = false;
        this.dragObjects = [];
        this.dragOffsets = new Map();
    }
    onMouseDown(event) {
        this.mouseStartPos.set(event.screenX, event.screenY);
        const intersect = this.getAssetIntersect(event);
        if (intersect && intersect.object.isSelected()) {
            this.controls.enabled = false;
            this.plane.setFromNormalAndCoplanarPoint(this.pNormal, intersect.point);
            this.isDragging = true;
            this.dragObjects = this.ctx.listAssets().filter(x => x.isSelected());
            this.dragOffsets.clear();
            // Store per-object shift (distance from object to the intersection point)
            this.dragObjects.forEach(obj => {
                const shift = new THREE.Vector3().subVectors(obj.position, intersect.point);
                this.dragOffsets.set(obj, shift);
            });
        }
    }
    onMouseMove(event) {
        var _a, _b;
        this.initRaycaster(event);
        let moveAxis = 'both';
        if (((_a = this.keyEvent) === null || _a === void 0 ? void 0 : _a.key) == 'x') {
            moveAxis = 'x';
        }
        else if (((_b = this.keyEvent) === null || _b === void 0 ? void 0 : _b.key) == 'z') {
            moveAxis = 'z';
        }
        if (this.isDragging && this.dragObjects.length) {
            this.raycaster.ray.intersectPlane(this.plane, this.planeIntersect);
            this.dragObjects.forEach(obj => {
                const shift = this.dragOffsets.get(obj);
                if (shift) {
                    const newPos = new THREE.Vector3().addVectors(this.planeIntersect, shift);
                    // Constrain movement by selected axis
                    switch (moveAxis) {
                        case 'x':
                            this.ctx.updateAssets([obj], { coordinate3dX: newPos.x });
                            break;
                        case 'z':
                            this.ctx.updateAssets([obj], { coordinate3dZ: newPos.z });
                            break;
                        case 'both':
                            this.ctx.updateAssets([obj], {
                                coordinate3dX: newPos.x, coordinate3dZ: newPos.z
                            });
                            break;
                    }
                }
            });
        }
    }
    onMouseUp(event) {
        var _a;
        this.isDragging = false;
        this.dragObjects = [];
        this.dragOffsets.clear();
        this.controls.enabled = true;
        // TODO: make this more stable, look for any movement during drag, and do not select if any key is pressed except controls
        const vec = new THREE.Vector2(event.screenX, event.screenY);
        const moveVector = this.mouseStartPos.sub(vec);
        //console.log('mouse up', this.mouseHoverAsset)
        let asset = this.mouseHoverAsset;
        if (!asset) {
            asset = this.getCursorAsset(event);
        }
        this.mouseHoverAsset = null;
        //const asset = this.mouseHoverAsset
        //console.log('key event', this.keyEvent)
        //if (!this.keyEvent?.key) {
        if (asset != null && moveVector.length() < 6) {
            if ((_a = this.keyEvent) === null || _a === void 0 ? void 0 : _a.shiftKey) {
                this.ctx.selectAsset(asset.state.id, 'toggle');
            }
            else {
                this.ctx.selectAsset(asset.state.id, 'replace');
            }
        }
        else if (moveVector.length() == 0) {
            this.ctx.clearSelection();
        }
        //}
        this.ctx.commit();
    }
}
export function createGround(size) {
    const groundGeo = new THREE.ConeGeometry(size, 0);
    const groundMat = new THREE.ShadowMaterial();
    groundMat.polygonOffset = true;
    groundMat.depthTest = true;
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.position.y = -0.1;
    ground.rotation.x = -Math.PI;
    ground.receiveShadow = true;
    ground.renderOrder = -10;
    return ground;
}
export function createSun(position) {
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
    dirLight.color.setHSL(0.1, 1, 0.95);
    dirLight.position.set(position.x, 2, position.z);
    dirLight.position.multiplyScalar(200);
    dirLight.castShadow = true;
    dirLight.shadow.camera.top = -200;
    dirLight.shadow.camera.bottom = 200;
    dirLight.shadow.mapSize.width = 4096;
    dirLight.shadow.mapSize.height = 4096;
    dirLight.shadow.camera.left = -200;
    dirLight.shadow.camera.right = 200;
    dirLight.shadow.camera.near = 0;
    dirLight.shadow.camera.far = 1000;
    return dirLight;
}
export function setupEnvironment(options) {
    let result = new THREE.Group();
    const env = options.environment;
    const hemiLight = new THREE.HemisphereLight(new THREE.Color(env.skyColor), new THREE.Color(env.groundColor), env.intensity);
    hemiLight.scale.set(100, 100, 100);
    hemiLight.position.set(0, 0, 0);
    let sun = createSun(new THREE.Vector3(1, -5, 1));
    sun.color = new THREE.Color(env.sunColor);
    sun.intensity = env.sunIntensity;
    result.add(sun);
    if (options.showGround) {
        let ground = createGround(1000);
        ground.material.opacity = env.shadowOpacity;
        result.add(ground);
    }
    return result;
}
export class MeasureDeviceObject extends THREE.Group {
    constructor(device) {
        super();
        this.device = device;
        this.userData.ignoreBBox = true;
    }
    drawShape() {
        this.clear();
        const measurePoints = this.device.measurePoints;
        let measurePointGeo = new THREE.BoxGeometry(0.5, 1, 0.5);
        let mat = new THREE.MeshPhongMaterial({ color: 0xa1dfff, transparent: false });
        for (let measurePoint of measurePoints) {
            var measurePointMesh = new THREE.Mesh(measurePointGeo, mat);
            let pos = measurePoint.pos;
            measurePointMesh.position.set(pos.posX, pos.posZ, pos.posY);
            measurePointMesh.userData.ignoreBBox = true;
            this.add(measurePointMesh);
        }
        measurePoints.map(mp => {
            let ray_material = new THREE.MeshBasicMaterial({ color: 0xff4747, transparent: false });
            let ray_first_vector = new THREE.Vector3(mp.pos.posX, mp.pos.posZ, mp.pos.posY);
            let ray_second_vector;
            let airLevel = this.device.airLevels['air-level-' + mp.id];
            if (airLevel != null) {
                let airBottom = Math.max(mp.pos.posZ - airLevel, 0.0);
                ray_second_vector = new THREE.Vector3(mp.pos.posX, airBottom, mp.pos.posY);
                let ray_path = new THREE.LineCurve3(ray_first_vector, ray_second_vector);
                let ray_geometry = new THREE.TubeGeometry(ray_path, 20, 0.05, 32, false);
                let ray = new THREE.Mesh(ray_geometry, ray_material);
                ray.userData.ignoreBBox = true;
                this.add(ray);
            }
        });
    }
}
class SensorHighlighter extends THREE.Group {
    constructor(center_diamter, length, base_radius) {
        super();
        this.center_diamter = center_diamter;
        this.length = length;
        this.base_radius = base_radius;
        let center_radius = center_diamter * 0.5;
        this.material = new THREE.MeshPhongMaterial({ color: 0x0 });
        this.material.transparent = false;
        let pointer_geometry = new THREE.ConeGeometry(base_radius, length, 8);
        let pointer1 = new THREE.Mesh(pointer_geometry, this.material);
        pointer1.rotateZ(Math.PI);
        pointer1.position.setY(center_radius + length * 0.5);
        let pointer2 = new THREE.Mesh(pointer_geometry, this.material);
        pointer2.rotateZ(Math.PI * 0.5);
        pointer2.position.setX(center_radius + length * 0.5);
        let pointer3 = new THREE.Mesh(pointer_geometry, this.material);
        pointer3.position.setY(-(center_radius + length * 0.5));
        let pointer4 = new THREE.Mesh(pointer_geometry, this.material);
        pointer4.rotateZ(-Math.PI * 0.5);
        pointer4.position.setX(-(center_radius + +length * 0.5));
        this.add(pointer1);
        this.add(pointer2);
        this.add(pointer3);
        this.add(pointer4);
        //this.objects.position.set(new THREE.Vector3(0, 0, 0));
        this.position.x = 0;
        this.position.y = 2;
        this.position.z = 0;
        this.rotateX(Math.PI * 0.5);
        this.hide();
    }
    hide() {
        this.visible = false;
    }
    ;
    show() {
        this.visible = true;
    }
    ;
    showAt(position) {
        this.position.x = position.x;
        this.position.y = position.y;
        this.position.z = position.z;
        this.show();
    }
}
export class SensorObject extends THREE.Group {
    constructor(telemetryType, telemetryLimits, options) {
        super();
        this.telemetryType = telemetryType;
        this.telemetryLimits = telemetryLimits;
        this.options = options;
        this.material = new THREE.MeshPhongMaterial();
        this.color = tinycolor(this.options.colors.disableColor); // THREE.Color = new THREE.Color()
        this.isDisabled = false;
        if (telemetryType == 'temperature') {
            this.telemetryColors = options.colors.tempMatrixColors;
        }
        else if (telemetryType == 'moisture') {
            this.telemetryColors = options.colors.moistureMatrixColors;
        }
        else if (telemetryType == 'co2') {
            this.telemetryColors = options.colors.co2MatrixColors;
        }
        else
            throw new Error('unknown telemetry type ' + telemetryType);
    }
    drawSensor(n_sensor_pos, sensor_geometry) {
        let geometry = sensor_geometry;
        this.material.transparent = false;
        let mesh = new THREE.Mesh(geometry, this.material);
        this.position.x = n_sensor_pos.x;
        this.position.y = n_sensor_pos.y;
        this.position.z = n_sensor_pos.z;
        this.updateMaterial();
        this.add(mesh);
        return mesh;
    }
    updateSensorColor(value) {
        this.color = this.mapTempToColor(value);
        this.updateMaterial();
    }
    ;
    mapTempToColor(sensorValue) {
        const colors = this.options.colors;
        if (sensorValue == 'E' || sensorValue == null) {
            return tinycolor(colors.errorValueColor);
        }
        else if (sensorValue == 'T' && this.telemetryType == 'moisture') {
            return tinycolor(colors.disableColor);
        }
        else {
            let color;
            let val = Number(sensorValue);
            if (val <= this.telemetryLimits[0]) {
                color = tinycolor(this.telemetryColors[0]);
            }
            else if (val >= this.telemetryLimits[this.telemetryLimits.length - 1]) {
                color = tinycolor(this.telemetryColors[this.telemetryColors.length - 1]);
            }
            else {
                let firstIndex = 0;
                let secondIndex = 0;
                for (let i = 0; i < this.telemetryLimits.length; i++) {
                    if (val <= this.telemetryLimits[i + 1]) {
                        firstIndex = i;
                        secondIndex = i + 1;
                        break;
                    }
                }
                let firstColor = tinycolor(this.telemetryColors[firstIndex]);
                let secondColor = tinycolor(this.telemetryColors[secondIndex]);
                let percent = ((val - this.telemetryLimits[firstIndex]) /
                    (this.telemetryLimits[secondIndex] - this.telemetryLimits[firstIndex])) * 100;
                color = tinycolor.mix(firstColor, secondColor, percent);
            }
            return color.setAlpha(0.75);
        }
    }
    ;
    getPosition() {
        return this.position;
    }
    ;
    updateMaterial() {
        if (!this.isDisabled) {
            let color = new THREE.Color('#' + this.color.toHex());
            let material = this.material;
            this.material.color = color;
            this.material.emissive = color;
            this.material.opacity = 1.0;
            this.material.transparent = false;
        }
        else {
            this.material.color = new THREE.Color(this.options.sensor.disableColor);
            this.material.emissive = new THREE.Color(this.options.sensor.disableColor);
            this.material.transparent = true;
            this.material.opacity = this.options.sensor.disableOpacity;
        }
    }
    enable() {
        this.isDisabled = false;
        this.updateMaterial();
    }
    disable() {
        this.isDisabled = true;
        this.updateMaterial();
    }
}
export class CableObject extends THREE.Group {
    constructor(cableGeometry, options) {
        super();
        this.cableGeometry = cableGeometry;
        this.options = options;
        this.material = new THREE.MeshPhongMaterial();
        this.sensors = {};
    }
    drawShape() {
        this.drawCableLine();
    }
    drawLabel() {
        let labelHtml = document.createElement('div');
        labelHtml.classList.add('entity-label');
        labelHtml.textContent = this.cableGeometry.label; // this.options.label || this.getLabelString()
        let color = 'black';
        const style = {
            //"border": 'solid ' + color,
            //"borderRadius": '10px',
            "color": color,
            "backgroundColor": '#fff',
            "paddingLeft": '2px',
            "paddingRight": '2px',
            "paddingTop": '2px',
            "paddingBottom": '2px',
            "fontFamily": "'Roboto', serif",
            "fontWeight": "500",
            "whiteSpace": "nowrap",
            "textAlign": "center",
            "fontSize": "12px"
        };
        for (let k in style) {
            // @ts-ignore
            labelHtml.style[k] = style[k];
        }
        let label = new CSS2DObject(labelHtml);
        let base_position = this.cableGeometry.basePosition;
        let pos = new THREE.Vector3(base_position.x, base_position.y, base_position.z);
        label.position.set(pos.x, pos.y, pos.z);
        this.add(label);
        //result.add(label)
    }
    drawCableLine() {
        this.clear();
        if (this.options.showCableLabels) {
            this.drawLabel();
        }
        this.sensors = {};
        // base_position, length, n_sensors, sensor_distance, sensor_offset, sensor_geometry
        let base_position = this.cableGeometry.basePosition;
        let length = this.cableGeometry.length;
        let sensor_distance = this.cableGeometry.sensorDistance;
        let sensor_offset = this.cableGeometry.sensorOffset;
        let n_sensors = this.cableGeometry.numSensors;
        let sensor_geometry = this.cableGeometry.sensorGeometry;
        // Tube line
        let path = new THREE.LineCurve3(base_position, new THREE.Vector3(base_position.x, base_position.y - length, base_position.z));
        let tube_geometry = new THREE.TubeGeometry(path, 20, 0.1, 32, false);
        let cable_mesh = new THREE.Mesh(tube_geometry, this.material);
        this.add(cable_mesh);
        let available_sensors = this.cableGeometry.availableSensors;
        let sp = new THREE.Vector3(base_position.x, base_position.y - length + sensor_offset, base_position.z);
        for (let i = 0; i < n_sensors; i++) {
            let step_y = i * sensor_distance;
            let n_sensor_pos = new THREE.Vector3(sp.x, sp.y + step_y, sp.z);
            if (!available_sensors || available_sensors.includes(i + 1)) {
                let sensor = new SensorObject(this.options.telemetryType || 'temperature', this.options.telemetryLimits || [], this.options);
                this.add(sensor);
                this.sensors[i] = sensor;
                sensor.drawSensor(n_sensor_pos, sensor_geometry);
            }
        }
    }
    updateSensorColors(data) {
        var _a;
        for (var i in this.sensors) {
            (_a = this.sensors[i]) === null || _a === void 0 ? void 0 : _a.updateSensorColor(data[i]);
        }
    }
    getSensorPosition(sensorIndex) {
        var _a;
        return (_a = this.sensors[sensorIndex]) === null || _a === void 0 ? void 0 : _a.getPosition();
    }
    disableSensors(sensorIndexes) {
        var _a;
        for (var i in this.sensors) {
            this.sensors[i].enable();
        }
        for (let i = 0; i < sensorIndexes.length; i++) {
            (_a = this.sensors[sensorIndexes[i]]) === null || _a === void 0 ? void 0 : _a.disable();
        }
    }
}
export class CropObject extends THREE.Group {
    constructor() {
        super();
        this.cropLevels = {};
        this.measurePoints = [];
    }
    redraw() {
    }
}
class BaseAssetObject extends THREE.Group {
    constructor(options) {
        super();
        this.options = options;
        this.selectIndicator = null;
        this.levelIndicators = [];
        this.selected = false;
        this.material = this.createMaterial();
        this.selectMaterial = this.createSelectMaterial();
    }
    redraw() {
        this.clear();
        let shape = this.drawShape(this.material, 0);
        shape.castShadow = true;
        shape.receiveShadow = true;
        this.add(shape);
    }
    initSelectIndicator(visible) {
        if (this.selectIndicator == null) {
            this.selectIndicator = this.drawSelectionIndicator();
            this.selectIndicator.receiveShadow = true;
            this.selectIndicator.position.y = 0.1;
            this.add(this.selectIndicator);
        }
        this.selectIndicator.visible = this.options.showSelection && visible;
    }
    select() {
        this.selected = true;
        this.initSelectIndicator(true);
    }
    deselect() {
        this.selected = false;
        this.initSelectIndicator(false);
    }
    toggleSelect() {
        if (this.selected) {
            this.deselect();
        }
        else {
            this.select();
        }
    }
    createMaterialSettings(opt) {
        let params = Object.assign(Object.assign({}, DEFAULT_MATERIAL), opt);
        delete params['alarmEmissive'];
        return params;
    }
    createSelectMaterial() {
        const color = new THREE.Color(this.options.selection.color);
        let material = new THREE.MeshLambertMaterial({ color: color, emissive: color });
        material.polygonOffset = true;
        material.depthTest = true;
        material.transparent = false;
        return material;
    }
    createMaterial() {
        let params = this.createMaterialSettings(this.options.material);
        let material = new THREE.MeshPhysicalMaterial(params);
        material.emissive = new THREE.Color(this.options.general.siloEmissive);
        material.opacity = 0.0;
        material.transparent = false;
        if (this.options.showCables || this.options.showCrop || this.options.showVolume) {
            if (this.options.siloBackface) {
                material.side = THREE.BackSide;
            }
            else {
                material.side = THREE.FrontSide;
            }
            material.opacity = this.options.siloOpacity;
            material.transparent = true;
        }
        if (isAlarm(this.options)) {
            material.color = new THREE.Color(this.options.colors.errorColor);
            material.emissive = new THREE.Color(this.options.general.siloAlarmEmissive);
        }
        material.polygonOffset = true;
        material.depthTest = true;
        return material;
    }
    viewRotate(rotation) {
        this.rotateY(toRadians(rotation));
    }
    updateSensorValues(sensorsData) { }
    setDisabledSensors(disabledSensors) { }
    hightlightSensor(cableNum, sensorIndex) { }
    updateCrop(crop) { }
    deHightlightSensors() { }
}
class AerationObject extends BaseAssetObject {
    constructor(geometry, options) {
        super(options);
        this.geometry = geometry;
        this.options = options;
    }
    getBound() {
        let _geo = this.geometry;
        let scale = _geo.scale || 1;
        return {
            'width': _geo.width * scale, 'height': _geo.height * scale, 'length': _geo.length * scale
        };
    }
    drawShape(mat, margin) {
        let bound = this.getBound();
        let radius = bound.width * 0.5;
        let result = new THREE.Group();
        let top = new THREE.CylinderGeometry(radius, radius, bound.length);
        top.rotateZ(-Math.PI / 2);
        top.translate(0, bound.height, 0);
        result.add(new THREE.Mesh(top, mat));
        let geom = new THREE.BoxGeometry(bound.length, bound.height, bound.width);
        geom.translate(0, bound.height * 0.5, 0);
        result.add(new THREE.Mesh(geom, mat));
        return result; // TODO: typing, groups are not allowed as shapes
    }
    drawSelectionIndicator() {
        const size = this.options.selection.width * 2;
        const bound = this.getBound();
        let geo = new THREE.PlaneGeometry(bound.length + size, bound.width + size);
        geo.rotateX(-Math.PI / 2);
        return new THREE.Mesh(geo, this.selectMaterial);
    }
}
class WeatherStationObject extends BaseAssetObject {
    constructor(geometry, options) {
        super(options);
        this.geometry = geometry;
        this.options = options;
    }
    drawShape(mat, margin) {
        let geom = new THREE.SphereGeometry(this.geometry.size);
        let mesh = new THREE.Mesh(geom, mat);
        mesh.position.y = this.geometry.size;
        return mesh;
    }
    drawSelectionIndicator() {
        const radius = this.geometry.size;
        let geo = new THREE.RingGeometry(radius, radius + this.options.selection.width);
        geo.rotateX(-Math.PI / 2);
        return new THREE.Mesh(geo, this.selectMaterial);
    }
}
export class AssetGroupObject extends BaseAssetObject {
    constructor(geometry, options) {
        super(options);
        this.geometry = geometry;
        this.options = options;
    }
    drawShape(mat, margin) {
        const siloGroupGeometry = this.geometry;
        let box_geometry = new THREE.BoxGeometry(siloGroupGeometry.length, siloGroupGeometry.height, siloGroupGeometry.width);
        let box = new THREE.Mesh(box_geometry, mat);
        box.position.y = siloGroupGeometry.height * 0.5;
        return box;
    }
    drawSelectionIndicator() {
        const size = this.options.selection.width * 2;
        const width = this.geometry.width + size;
        const length = this.geometry.length + size;
        let geo = new THREE.PlaneGeometry(length, width);
        geo.rotateX(-Math.PI / 2);
        return new THREE.Mesh(geo, this.selectMaterial);
    }
}
class AssetObject extends BaseAssetObject {
    constructor(siloGeometry, sensorLinesGeometry, cropGeometry, options) {
        super(options);
        this.siloGeometry = siloGeometry;
        this.sensorLinesGeometry = sensorLinesGeometry;
        this.cropGeometry = cropGeometry;
        this.options = options;
        this.highlighter = new SensorHighlighter(2.0, 2.0, 1.0);
        this.cables = {};
        this.sensorsData = {};
        this.disabledSensors = {};
        this.cropValues = {};
        this.material = this.createMaterial();
        this.grainMaterial = this.createGrainMaterial();
    }
    createGrainMaterial() {
        var _a;
        let params = Object.assign(Object.assign({}, DEFAULT_MATERIAL), this.options.material);
        let material = new THREE.MeshPhysicalMaterial(params);
        material.color = new THREE.Color(this.options.general.cropColor);
        material.emissive = new THREE.Color(this.options.general.cropEmissive);
        material.polygonOffset = true;
        material.depthTest = true;
        material.transparent = false;
        if ((_a = this.options.crop) === null || _a === void 0 ? void 0 : _a.opacity) {
            material.transparent = true;
            material.opacity = this.options.crop.opacity;
        }
        return material;
    }
    updateSensorValues(sensorsData) {
        this.sensorsData = sensorsData;
        for (let key in sensorsData) {
            if (key in this.cables) {
                this.cables[key].updateSensorColors(sensorsData[key]);
            }
        }
    }
    setDisabledSensors(disabledSensors) {
        this.disabledSensors = disabledSensors;
        for (let key in disabledSensors) {
            if (key in this.cables) {
                this.cables[key].disableSensors(disabledSensors[key]);
            }
        }
    }
    updateCrop(crop) {
        this.cropGeometry = crop;
        this.redraw();
    }
    hightlightSensor(cableNum, sensorIndex) {
        var _a;
        this.deHightlightSensors();
        let pos = (_a = this.cables[cableNum]) === null || _a === void 0 ? void 0 : _a.getSensorPosition(sensorIndex);
        if (pos == null)
            return this.options.logger.warn('sensor not found, unable to highlight sensor: ', cableNum, sensorIndex);
        this.highlighter.showAt(pos);
    }
    deHightlightSensors() {
        this.highlighter.hide();
    }
    createCables() {
        const siloGeometry = this.siloGeometry;
        // Temperature Sensor Lines
        let sensor_radius = this.options.sensor.radius;
        let sensor_geometry = new THREE.SphereGeometry(sensor_radius, 8, 8);
        let options = this.options;
        for (let _sl of this.sensorLinesGeometry) {
            // copy sl so we can mutate without side-effects
            let sl = Object.assign({}, _sl);
            let all_sensors = [];
            for (let i = 1; i <= sl.sensorCount; i++) {
                all_sensors.push(i);
            }
            let available_sensors = [];
            if (options.telemetryType == 'co2') {
                available_sensors = sl.co2Sensors || [];
            }
            else if (options.telemetryType == 'moisture') {
                available_sensors = sl.moistureSensors || [];
            }
            else if (sl.type == 'co2_sensor_lines') {
                // TODO: this is a hack, 
                // we usally don't set temperatureSensors and assume that all moisture-sensors also are temperature sensors
                // however this is not true for co2_sensor_lines
                available_sensors = sl.co2Sensors || all_sensors;
            }
            else {
                available_sensors = sl.temperatureSensors || all_sensors;
            }
            if (available_sensors.length == 0) {
                continue;
            }
            // hackfix for buildings
            let geom = this.siloGeometry;
            if (isBuilding(geom)) {
                sl.positionX -= (geom.length / 2);
                sl.positionY -= (geom.width / 2);
            }
            // Use the center of the eave as a base / origin
            let cable_origin = new THREE.Vector3(0.0, siloGeometry.eaveheight, 0.0);
            let ceiling_offset = this.computeCeilingOffset(sl.positionX, sl.positionY, sl.positionZ);
            let cable_offset_snap_to_sealing = new THREE.Vector3(0, ceiling_offset, 0);
            let cable_offset = new THREE.Vector3(sl.positionX, sl.positionZ, sl.positionY);
            let base_position = cable_origin.add(cable_offset).add(cable_offset_snap_to_sealing);
            let max_length = siloGeometry.eaveheight + cable_offset_snap_to_sealing.y;
            let line_length = Math.min(sl.length, max_length);
            let sl_object = new CableObject({
                basePosition: base_position, length: line_length, label: _sl.lineNum.toString(),
                numSensors: sl.sensorCount, sensorDistance: sl.sensorDistance,
                sensorOffset: 0.0, sensorGeometry: sensor_geometry,
                availableSensors: available_sensors
            }, this.options);
            sl_object.drawShape();
            this.add(sl_object);
            this.cables[sl.lineNum] = sl_object;
        }
    }
    redraw() {
        let opt = this.options;
        this.clear();
        let shape = this.drawShape(this.material, 0);
        shape.castShadow = true;
        shape.receiveShadow = true;
        this.add(shape);
        this.material.opacity = opt.siloOpacity;
        if (opt.showCables) {
            this.createCables();
            this.add(this.highlighter);
        }
        this.levelIndicators.map(lvl => {
            let measureDevices = new MeasureDeviceObject(lvl);
            measureDevices.drawShape();
            this.add(measureDevices);
        });
        if (opt.showCrop) {
            let grainMesh = this.drawGrain();
            this.add(grainMesh);
        }
        if (isCylinder(this.siloGeometry) && opt.showVolume) {
            this.add(this.drawVolume(this.siloGeometry));
        }
        this.updateSensorValues(this.sensorsData);
        this.setDisabledSensors(this.disabledSensors);
    }
    drawVolume(geo) {
        return new THREE.Group();
    }
    drawGrainSurface(mat) {
        if (!this.cropGeometry.cropPoints)
            return null;
        let cropPoints = this.cropGeometry.cropPoints;
        if (typeof cropPoints == 'string') {
            try {
                cropPoints = JSON.parse(cropPoints);
            }
            catch (err) {
                console.error(err);
                return null;
            }
        }
        if (cropPoints.length < 1)
            return null;
        let newControlPoints = {};
        // NOTE: must make sure two points does not have same position, otherwise it won't display anything
        cropPoints.map(p => {
            let roundP = [Math.round(p[0] * 100) / 100, Math.round(p[1] * 100) / 100, Math.round(p[2] * 100) / 100];
            newControlPoints[roundP.toString()] = roundP;
            if (this.options.crop.showControlPoints) {
                let helperGeo = new THREE.SphereGeometry(0.3);
                let levelHelper = new THREE.Mesh(helperGeo, new THREE.MeshLambertMaterial({ color: 0x0 }));
                levelHelper.position.set(p[0], p[2], p[1]);
                this.add(levelHelper);
            }
        });
        let controlPoints = Object.values(newControlPoints);
        if (controlPoints.length < 1)
            return null;
        let values = controlPoints.map(p => p[2]);
        var rbf = new RBFInterpolator(controlPoints, values, "cubic", 1.0);
        let plane = this.grainSurface();
        plane.rotateX(-Math.PI / 2);
        let minValue = Math.min(...values);
        let maxValue = Math.max(...values);
        let positions = plane.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
            let h = rbf.evaluate(positions[i], positions[i + 2]);
            if (this.options.crop.clamp) {
                h = Math.max(minValue, Math.min(h, maxValue));
            }
            if (!isNaN(h)) {
                positions[i + 1] = h;
            }
        }
        plane.attributes.position.needsUpdate = true;
        flipNormals(plane);
        let planeMesh = new THREE.Mesh(plane, mat);
        if (this.options.crop.showWireframe) {
            let wireMat = new THREE.MeshLambertMaterial({
                wireframe: true, opacity: 0.5
            });
            let wire = new THREE.Mesh(plane, wireMat);
            this.add(wire);
        }
        planeMesh.updateMatrix();
        return planeMesh;
    }
    drawGrain() {
        /*if (this.cropGeometry.algorithm != 'rbf') {
          return this.drawSimpleGrain()
        }*/
        let mat = this.grainMaterial;
        const margin = this.cropGeometry.margin;
        let tubeMesh = this.drawShape(mat, margin);
        let planeMesh = null;
        try {
            planeMesh = this.drawGrainSurface(this.grainMaterial);
        }
        catch (err) {
            console.error(err);
        }
        if (planeMesh == null)
            return new THREE.Group();
        const intersect = CSG.subtract(tubeMesh, planeMesh);
        intersect.receiveShadow = true;
        intersect.castShadow = true;
        return intersect;
    }
    drawSimpleGrain() {
        return new THREE.Mesh();
    }
}
export class SiloObject extends AssetObject {
    get baseOffset() {
        return this.siloGeometry.hopperheight ? this.siloGeometry.hopperheight : 0;
    }
    getElipson() {
        return this.siloGeometry.diameter * 0.7;
    }
    computeCeilingOffset(x, y, z) {
        const geo = this.siloGeometry;
        let r = Math.sqrt(x * x + y * y);
        let h = geo.totalheight - geo.eaveheight;
        let R = geo.diameter * 0.5;
        let ceiling_offset = h * (R - r) / R;
        return ceiling_offset;
    }
    bottomRadius() {
        let geo = this.siloGeometry;
        let radius = geo.diameter * 0.5;
        let bottomRadius = radius;
        if (geo.hopper && geo.hopperangle && geo.hopperheight) {
            let alpha = toRadians(geo.hopperangle);
            let alpha_off_r = geo.hopperheight * Math.tan(alpha);
            let radius_top = geo.diameter * 0.5;
            bottomRadius = radius_top - alpha_off_r;
        }
        return bottomRadius;
    }
    drawSelectionIndicator() {
        const radius = this.bottomRadius();
        let geo = new THREE.RingGeometry(radius, radius + this.options.selection.width);
        geo.rotateX(-Math.PI / 2);
        return new THREE.Mesh(geo, this.selectMaterial);
    }
    grainSurface() {
        let D = this.siloGeometry.diameter;
        let theta = Math.max(Math.min(D * 4, 50), 10);
        let phi = Math.max(Math.min(D, 20), 5);
        return new THREE.RingGeometry(0, D * 0.5 + 1, Math.round(theta), Math.round(phi));
    }
    drawShape(mat, margin) {
        let geo = this.siloGeometry;
        let baseOffset = this.baseOffset;
        let radius = geo.diameter * 0.5;
        let bottomRadius = this.bottomRadius();
        let points = [
            new THREE.Vector2(0, 0),
            new THREE.Vector2(bottomRadius - margin, margin)
        ];
        if (baseOffset > 0) {
            points.push(new THREE.Vector2(radius - margin, baseOffset));
        }
        points.push(new THREE.Vector2(radius - margin, geo.eaveheight), new THREE.Vector2(0, geo.totalheight - margin));
        const geometry = new THREE.LatheGeometry(points, 50);
        const lathe = new THREE.Mesh(geometry, mat);
        lathe.receiveShadow = true;
        lathe.castShadow = true;
        lathe.updateMatrix();
        return lathe;
    }
}
export class BuildingObject extends AssetObject {
    getElipson() {
        return this.siloGeometry.length * 0.7;
    }
    drawSelectionIndicator() {
        const size = this.options.selection.width * 2;
        const width = this.siloGeometry.width + size;
        const length = this.siloGeometry.length + size;
        let geo = new THREE.PlaneGeometry(length, width);
        geo.rotateX(-Math.PI / 2);
        return new THREE.Mesh(geo, this.selectMaterial);
    }
    grainSurface() {
        return new THREE.PlaneGeometry(this.siloGeometry.length + 1, this.siloGeometry.width + 1, this.siloGeometry.length, this.siloGeometry.width);
    }
    computeCeilingOffset(x, y, z) {
        const geo = this.siloGeometry;
        let h = geo.totalheight - geo.eaveheight;
        let distance_to_cable = geo.width * 0.5 - Math.abs(y);
        return h * distance_to_cable / (geo.width * 0.5);
    }
    get diameter() {
        return Math.max(this.siloGeometry.width, this.siloGeometry.length);
    }
    drawShape(mat, margin) {
        const siloGeometry = this.siloGeometry;
        let geometry = new THREE.BoxGeometry(siloGeometry.length, siloGeometry.eaveheight, siloGeometry.width, 2, 2, 2);
        geometry.translate(0, siloGeometry.eaveheight / 2, 0);
        for (let i = 0; i < geometry.attributes.position.array.length; i += 3) {
            // a single vertex Y position
            //let xpos = geometry.attributes.position.array[i]
            let ypos = geometry.attributes.position.array[i + 1];
            let zpos = geometry.attributes.position.array[i + 2];
            if (ypos == siloGeometry.eaveheight && zpos == 0) {
                // @ts-ignore
                geometry.attributes.position.array[i + 1] = siloGeometry.totalheight;
            }
        }
        geometry.attributes.position.needsUpdate = true;
        let mesh = new THREE.Mesh(geometry, mat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        return mesh;
    }
}
